import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useOrg } from '@/context/OrgContext'
import type { Activity, Profile, RolNivel, Tour } from '@/types'

export interface OrgMember {
  user_id: string
  rol_nivel: RolNivel
  profile: Profile | null
}

// ── Tours ────────────────────────────────────────────────────────────────────

export function useAllTours() {
  const { activeOrg } = useOrg()
  return useQuery({
    queryKey: ['all-tours', activeOrg?.id],
    enabled: !!activeOrg,
    queryFn: async (): Promise<Tour[]> => {
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .eq('org_id', activeOrg!.id)
        .order('fecha_inicio', { ascending: false })

      if (error) throw error
      return data ?? []
    },
  })
}

// ── Activities ───────────────────────────────────────────────────────────────

export function useActivitiesByTour(tourId: string | undefined) {
  return useQuery({
    queryKey: ['activities', tourId],
    enabled: !!tourId,
    queryFn: async (): Promise<Activity[]> => {
      const { data, error } = await supabase
        .from('activities')
        .select('*, activity_links(*)')
        .eq('tour_id', tourId!)
        .order('start_time_utc', { ascending: true })

      if (error) throw error
      return (data ?? []) as Activity[]
    },
    // Subscribe to realtime changes
    refetchInterval: false,
  })
}

// "My agenda" — future activities across every tour of the active org that the
// user is allowed to see (RLS filters by membership + visibility). Joined with
// tour name so we can label each card. Inner join so `.eq('tour.org_id', …)`
// works as a filter.
export type MyAgendaActivity = Activity & {
  tour: { id: string; nombre: string; estado: 'active' | 'archived' }
}

export function useMyAgenda(orgId: string | undefined) {
  return useQuery({
    queryKey: ['my-agenda', orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<MyAgendaActivity[]> => {
      // Go back ~6h from "now" so crew don't lose an in-progress activity
      // just because it started earlier in the day.
      const sinceIso = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
      const { data, error } = await supabase
        .from('activities')
        .select('*, activity_links(*), tour:tours!inner(id, nombre, estado, org_id)')
        .eq('tour.org_id', orgId!)
        .gte('start_time_utc', sinceIso)
        .order('start_time_utc', { ascending: true })
        .limit(200)
      if (error) throw error
      return (data ?? []) as unknown as MyAgendaActivity[]
    },
  })
}

// Realtime subscription for activities
export function useActivitiesRealtime(tourId: string | undefined) {
  const queryClient = useQueryClient()

  return {
    subscribe: () => {
      if (!tourId) return null

      const channel = supabase
        .channel(`activities-${tourId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'activities',
            filter: `tour_id=eq.${tourId}`,
          },
          () => {
            queryClient.invalidateQueries({ queryKey: ['activities', tourId] })
          }
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            queryClient.invalidateQueries({ queryKey: ['activities', tourId] })
          }
        })

      return () => supabase.removeChannel(channel)
    },
  }
}

// ── Mutations (manager only) ─────────────────────────────────────────────────

export function useCreateTour() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { org_id: string; nombre: string; fecha_inicio: string; fecha_fin: string }) => {
      const { data: tour, error } = await supabase
        .from('tours')
        .insert(data)
        .select()
        .single()

      if (error) throw error
      return tour
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-tours'] })
      queryClient.invalidateQueries({ queryKey: ['available-tours'] })
    },
  })
}

// Atomic save (create or update) for an activity + its links + its
// assignments. Wraps the save_activity Postgres RPC (migration 016) which
// runs everything in a single transaction. Replaces the old
// useCreateActivity / useUpdateActivity pair that did multiple separate
// writes from the client — those could leave the activity half-saved if
// any but the first write failed.
export function useSaveActivity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      tourId,
      titulo,
      descripcion,
      venueName,
      venueTimezone,
      startTimeUtc,
      endTimeUtc,
      visibilityLevel,
      links,
      assignedUserIds,
    }: {
      id: string | null  // null = create
      tourId: string
      titulo: string
      descripcion: string | null
      venueName: string | null
      venueTimezone: string
      startTimeUtc: string
      endTimeUtc: string | null
      visibilityLevel: 'public' | 'assigned'
      links: { titulo: string; url: string; tipo: string }[]
      assignedUserIds: string[]
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('save_activity', {
        p_activity_id: id,
        p_tour_id: tourId,
        p_titulo: titulo,
        p_descripcion: descripcion,
        p_venue_name: venueName,
        p_venue_timezone: venueTimezone,
        p_start_time_utc: startTimeUtc,
        p_end_time_utc: endTimeUtc,
        p_visibility_level: visibilityLevel,
        p_links: links,
        p_assigned_user_ids: assignedUserIds,
      })
      if (error) throw error
      return data as string
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['activities', variables.tourId] })
      queryClient.invalidateQueries({ queryKey: ['tour-assignments', variables.tourId] })
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ['activity-assignments', variables.id] })
      }
    },
  })
}

export function useDeleteActivity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, tourId }: { id: string; tourId: string }) => {
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['activities', variables.tourId] })
      queryClient.invalidateQueries({ queryKey: ['tour-assignments', variables.tourId] })
    },
  })
}

// The current user's role inside a specific tour. Takes priority over the
// org-level role wherever in-tour behavior is gated (e.g. artist "show all"
// toggle) — a manager can assign someone role 'B' on a tour even if their
// baseline org role is 'C', and that assignment is what should drive the UI
// inside that tour.
export function useMyTourRole(tourId: string | undefined, userId: string | undefined) {
  return useQuery({
    queryKey: ['my-tour-role', tourId, userId],
    enabled: !!tourId && !!userId,
    queryFn: async (): Promise<RolNivel | null> => {
      const { data, error } = await supabase
        .from('tour_memberships')
        .select('rol_nivel')
        .eq('tour_id', tourId!)
        .eq('user_id', userId!)
        .maybeSingle()
      if (error) throw error
      return (data?.rol_nivel as RolNivel) ?? null
    },
  })
}

// ── Org members & assignments ────────────────────────────────────────────────

// Members of the active org with their joined profile. Same query key as
// CrewView so cache is shared.
export function useOrgMembers(orgId: string | undefined) {
  return useQuery({
    queryKey: ['org-members', orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<OrgMember[]> => {
      const { data: rows, error } = await supabase
        .from('organization_memberships')
        .select('user_id, rol_nivel')
        .eq('org_id', orgId!)
        .order('rol_nivel', { ascending: true })
      if (error) throw error
      const members = (rows ?? []) as { user_id: string; rol_nivel: RolNivel }[]
      if (members.length === 0) return []

      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('*')
        .in('id', members.map(m => m.user_id))
      if (pErr) throw pErr

      const byId = new Map<string, Profile>((profiles ?? []).map(p => [p.id, p as Profile]))
      return members.map(m => ({
        user_id: m.user_id,
        rol_nivel: m.rol_nivel,
        profile: byId.get(m.user_id) ?? null,
      }))
    },
  })
}

// User_ids assigned to a specific activity (used by the form in edit mode).
export function useActivityAssignments(activityId: string | undefined) {
  return useQuery({
    queryKey: ['activity-assignments', activityId],
    enabled: !!activityId,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('activity_assignments')
        .select('user_id')
        .eq('activity_id', activityId!)
      if (error) throw error
      return (data ?? []).map(r => r.user_id)
    },
  })
}

// All assignments for a tour, mapped activity_id -> user_ids[]. Used by the
// schedule view to render the assigned-count chip per activity.
export function useTourAssignments(tourId: string | undefined) {
  return useQuery({
    queryKey: ['tour-assignments', tourId],
    enabled: !!tourId,
    queryFn: async (): Promise<Record<string, string[]>> => {
      const { data: acts, error: e1 } = await supabase
        .from('activities')
        .select('id')
        .eq('tour_id', tourId!)
      if (e1) throw e1
      if (!acts || acts.length === 0) return {}

      const { data, error } = await supabase
        .from('activity_assignments')
        .select('activity_id, user_id')
        .in('activity_id', acts.map(a => a.id))
      if (error) throw error

      const map: Record<string, string[]> = {}
      for (const row of (data ?? []) as { activity_id: string; user_id: string }[]) {
        if (!map[row.activity_id]) map[row.activity_id] = []
        map[row.activity_id].push(row.user_id)
      }
      return map
    },
  })
}
